import React, { useEffect, useRef, useContext } from 'react';
import { createUseStyles } from 'react-jss';
import HotspotMarker from '../../assets/lotties/HotspotMarker.json';
import lottie from 'lottie-web';
import TransitionText from '../common/TransitionText';
import { useState } from 'react';
import variables from '../../theme/variables';
import { State } from '../../contexts/GlobalState';

const useStyles = createUseStyles(
    {
        wrap: {
            width: 100,
            height: 100,
            position: 'relative',
            cursor: 'pointer',
        },

        marker: {
            width: 320,
            height: 320,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',

            '@media (min-width: 1024px)': {
                width: 214,
                height: 214,
            },
        },

        text: {
            position: 'absolute',
            top: 60,
            left: 100,

            fontSize: 24,
            lineHeight: '30px',
            textAlign: 'left',
            color: variables.palette.white,
            pointerEvents: 'none',

            '@media (min-width: 1024px)': {
                fontSize: 32,
                lineHeight: '40px',
            },
        },
    },
    {
        name: 'Hotspot',
    }
);

const firstAnimFrame = 15;
const firstLoopFrame = 32;
const lastLoopFrame = 120;

export default function Hotspot({
    distanceFromCenter,
    onClick,
    hotspot,
    type,
}) {
    const classes = useStyles();
    const { muted } = useContext(State);
    const title =
        type === 'primary' ? hotspot.hotspotTitle : hotspot.content.title;

    const audioRef = useRef();
    const lastDistance = useRef(10000);

    const element = useRef(null);
    const lottieInstance = useRef();

    const hovered = useRef(false);
    const [textState, setTextState] = useState('init'); // init | in | out

    useEffect(() => {
        if (element.current && !lottieInstance.current) {
            lottieInstance.current = lottie.loadAnimation({
                animationData: HotspotMarker,
                container: element.current,
                loop: false,
                autoplay: false,
            });
            lottieInstance.current.goToAndStop(firstAnimFrame, true);

            lottieInstance.current.addEventListener('enterFrame', (data) => {
                if (hovered.current && data.currentTime > lastLoopFrame) {
                    lottieInstance.current.goToAndPlay(firstLoopFrame, true);
                }
            });
        }
    }, []);

    function checkTime() {
        if (
            audioRef.current.currentTime >
            hotspot.sound.start + hotspot.sound.duration
        ) {
            audioRef.current.pause();
        } else {
            setTimeout(checkTime, 200);
        }
    }

    if (lastDistance.current > 100 && distanceFromCenter < 100) {
        audioRef.current.currentTime = hotspot.sound.start;
        checkTime();
        audioRef.current.volume = 0.2;
        audioRef.current.play();
    }
    lastDistance.current = distanceFromCenter;

    const handleClick = (e) => {
        onClick(hotspot);
    };

    const handleMouseEnter = () => {
        if (window.innerWidth < 1024) return;
        if (!lottieInstance.current) return;

        hovered.current = true;
        lottieInstance.current.goToAndPlay(firstAnimFrame, true);
        setTextState('in');
    };

    const handleMouseLeave = () => {
        if (window.innerWidth < 1024) return;
        if (!lottieInstance.current) return;

        hovered.current = false;
        lottieInstance.current.goToAndPlay(lastLoopFrame, true);
        setTextState('init');
    };

    return (
        <button
            className={classes.wrap}
            id={hotspot.id}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div
                className={classes.marker}
                ref={element}
                id={'animation-' + hotspot.id}
            ></div>
            <div className={classes.text}>
                <TransitionText
                    state={textState}
                    dangerouslySetInnerHTML={{ __html: title }}
                ></TransitionText>
            </div>
            <audio src={hotspot.sound.mp3} muted={muted} ref={audioRef} />
        </button>
    );
}
